import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  ReactNode,
} from 'react';
import { apiFetchUrl, resolveWebSocketUrl } from '../lib/api';
import { useAuth } from './AuthContext';
import {
  connectSupportSession,
  disconnectSupportSession,
  getSupportSession,
  SupportSession,
} from '../services/supportApi';

type Phase = 'idle' | 'waiting' | 'connected' | 'ended';

interface EngineerRemoteSupportContextValue {
  session: SupportSession | null;
  phase: Phase;
  loading: boolean;
  error: string | null;
  remoteVideoRef: React.RefObject<HTMLVideoElement | null>;
  loadSession: (sessionId: string) => Promise<void>;
  attachSession: (session: SupportSession) => void;
  disconnect: (reason?: string) => Promise<void>;
  sendControlEvent: (event: Record<string, unknown>) => void;
  clearError: () => void;
  remoteStream: MediaStream | null;
}

const EngineerRemoteSupportContext = createContext<EngineerRemoteSupportContextValue | null>(null);

function engineerMatchesUser(engineerId: string, user: { _id?: string; id?: string } | null | undefined): boolean {
  if (!user) return false;
  const target = String(engineerId);
  return [user._id, user.id].filter(Boolean).map(String).includes(target);
}

const HEARTBEAT_MS = 25_000;

function serializeIceCandidate(candidate: RTCIceCandidate): RTCIceCandidateInit {
  return candidate.toJSON ? candidate.toJSON() : (candidate as unknown as RTCIceCandidateInit);
}

function getSupportWsUrl(token: string) {
  return resolveWebSocketUrl(apiFetchUrl(`/ws/support?token=${encodeURIComponent(token)}`));
}

export function EngineerRemoteSupportProvider({ children }: { children: ReactNode }) {
  const { user, isAuthenticated } = useAuth();
  const [session, setSession] = useState<SupportSession | null>(null);
  const [phase, setPhase] = useState<Phase>('idle');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const heartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pcRef = useRef<RTCPeerConnection | null>(null);
  const remoteVideoRef = useRef<HTMLVideoElement | null>(null);
  const sessionRef = useRef<SupportSession | null>(null);
  const aliveRef = useRef(true);
  const reconnectRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingIceCandidatesRef = useRef<RTCIceCandidateInit[]>([]);

  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  const cleanupWebRtc = useCallback(() => {
    pcRef.current?.close();
    pcRef.current = null;
    pendingIceCandidatesRef.current = [];
    setRemoteStream(null);
    if (remoteVideoRef.current) remoteVideoRef.current.srcObject = null;
  }, []);

  const flushPendingIceCandidates = useCallback(async (pc: RTCPeerConnection) => {
    const pending = pendingIceCandidatesRef.current.splice(0);
    for (const candidate of pending) {
      try {
        await pc.addIceCandidate(new RTCIceCandidate(candidate));
      } catch {
        /* ignore stale candidates */
      }
    }
  }, []);

  const addRemoteIceCandidate = useCallback(async (pc: RTCPeerConnection, candidate: RTCIceCandidateInit) => {
    if (pc.remoteDescription) {
      await pc.addIceCandidate(new RTCIceCandidate(candidate));
      return;
    }
    pendingIceCandidatesRef.current.push(candidate);
  }, []);

  const cleanupWs = useCallback(() => {
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
    wsRef.current?.close();
    wsRef.current = null;
  }, []);

  const sendWs = useCallback((type: string, payload: Record<string, unknown>) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type, payload }));
    }
  }, []);

  const ensurePeerConnection = useCallback(
    async (s: SupportSession) => {
      if (pcRef.current) return pcRef.current;

      const pc = new RTCPeerConnection({
        iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
      });

      pc.ontrack = (ev) => {
        if (ev.streams[0]) {
          setRemoteStream(ev.streams[0]);
          if (remoteVideoRef.current) {
            remoteVideoRef.current.srcObject = ev.streams[0];
            void remoteVideoRef.current.play().catch(() => undefined);
          }
        }
      };

      pc.onicecandidate = (ev) => {
        if (ev.candidate) {
          sendWs('webrtc-signal', {
            sessionId: s.sessionId,
            engineerId: s.engineerId,
            customerUserId: s.customerUserId,
            signal: { candidate: serializeIceCandidate(ev.candidate) },
          });
        }
      };

      pcRef.current = pc;
      return pc;
    },
    [sendWs]
  );

  const joinSession = useCallback(
    async (s: SupportSession) => {
      setLoading(true);
      setError(null);
      try {
        const connected = await connectSupportSession(s.sessionId, s.sessionToken);
        setSession(connected);
        setPhase('connected');
        sessionRef.current = connected;
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Failed to connect remote session');
        setPhase('waiting');
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const attachSession = useCallback((s: SupportSession & { _id?: string }) => {
    const normalized = {
      ...s,
      sessionId: String(s.sessionId || s._id || ''),
      engineerId: String(s.engineerId),
      customerUserId: String(s.customerUserId),
    };
    if (!normalized.sessionId || normalized.sessionId === 'undefined') return;
    setSession(normalized);
    sessionRef.current = normalized;
    if (normalized.status === 'pending') {
      setPhase('waiting');
      void ensurePeerConnection(normalized);
    } else if (normalized.status === 'connected' || normalized.status === 'approved') {
      void ensurePeerConnection(normalized).then(() => joinSession(normalized));
    }
  }, [joinSession, ensurePeerConnection]);

  const loadSession = useCallback(
    async (sessionId: string) => {
      setLoading(true);
      setError(null);
      try {
        const s = await getSupportSession(sessionId);
        attachSession(s);
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Failed to load session');
      } finally {
        setLoading(false);
      }
    },
    [attachSession]
  );

  const connectWs = useCallback(
    (token: string) => {
      cleanupWs();
      const ws = new WebSocket(getSupportWsUrl(token));
      wsRef.current = ws;

      ws.onopen = () => {
        heartbeatRef.current = setInterval(() => sendWs('heartbeat', {}), HEARTBEAT_MS);
      };

      ws.onmessage = async (ev) => {
        try {
          const msg = JSON.parse(ev.data) as { type: string; payload?: Record<string, unknown> };
          const incoming = msg.payload?.session as SupportSession | undefined;
          const current = sessionRef.current;
          const s =
            incoming &&
            (!current || incoming.sessionId === current.sessionId || current.status === 'pending')
              ? incoming
              : incoming ?? current;

          switch (msg.type) {
            case 'support-request':
            case 'remote-support-request':
              if (s && engineerMatchesUser(s.engineerId, user)) {
                setSession(s);
                sessionRef.current = s;
                setPhase('waiting');
              }
              break;
            case 'support-approved':
            case 'remote-support-approved':
              if (s && engineerMatchesUser(s.engineerId, user)) {
                const normalized = {
                  ...s,
                  sessionId: String(s.sessionId),
                  engineerId: String(s.engineerId),
                  customerUserId: String(s.customerUserId),
                };
                setSession(normalized);
                sessionRef.current = normalized;
                await ensurePeerConnection(normalized);
                await joinSession(normalized);
              }
              break;
            case 'support-connected':
            case 'remote-session-started':
              if (s) {
                setSession(s);
                sessionRef.current = s;
                setPhase('connected');
              }
              break;
            case 'support-rejected':
            case 'remote-support-rejected':
            case 'support-disconnected':
            case 'remote-session-ended':
              cleanupWebRtc();
              setSession(null);
              sessionRef.current = null;
              setPhase('ended');
              break;
            case 'webrtc-signal': {
              const active = sessionRef.current;
              if (!active) break;
              const signal = msg.payload?.signal as Record<string, unknown> | undefined;
              if (!signal) break;
              const pc = await ensurePeerConnection(active);
              if (signal.type === 'offer' && signal.sdp) {
                await pc.setRemoteDescription(new RTCSessionDescription(signal as RTCSessionDescriptionInit));
                const answer = await pc.createAnswer();
                await pc.setLocalDescription(answer);
                sendWs('webrtc-signal', {
                  sessionId: active.sessionId,
                  engineerId: active.engineerId,
                  customerUserId: active.customerUserId,
                  signal: { type: 'answer', sdp: answer.sdp },
                });
                await flushPendingIceCandidates(pc);
              } else if (signal.type === 'answer' && signal.sdp) {
                await pc.setRemoteDescription(new RTCSessionDescription(signal as RTCSessionDescriptionInit));
                await flushPendingIceCandidates(pc);
              } else if (signal.candidate) {
                await addRemoteIceCandidate(pc, signal.candidate as RTCIceCandidateInit);
              }
              break;
            }
            default:
              break;
          }
        } catch {
          /* ignore malformed payloads */
        }
      };

      ws.onclose = () => {
        if (heartbeatRef.current) {
          clearInterval(heartbeatRef.current);
          heartbeatRef.current = null;
        }
        wsRef.current = null;
        if (aliveRef.current && isAuthenticated) {
          reconnectRef.current = setTimeout(() => {
            const token = localStorage.getItem('accessToken');
            if (token) connectWs(token);
          }, 3000);
        }
      };
    },
    [addRemoteIceCandidate, cleanupWebRtc, cleanupWs, ensurePeerConnection, flushPendingIceCandidates, joinSession, sendWs, user]
  );

  useEffect(() => {
    if (!isAuthenticated || !user) {
      aliveRef.current = false;
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      cleanupWs();
      cleanupWebRtc();
      return;
    }
    aliveRef.current = true;
    const token = localStorage.getItem('accessToken');
    if (token) connectWs(token);
    return () => {
      aliveRef.current = false;
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      cleanupWs();
      cleanupWebRtc();
    };
  }, [isAuthenticated, user, connectWs, cleanupWs, cleanupWebRtc]);

  const disconnect = useCallback(
    async (reason?: string) => {
      if (!session) return;
      setLoading(true);
      try {
        await disconnectSupportSession(session.sessionId, reason);
        cleanupWebRtc();
        setSession(null);
        sessionRef.current = null;
        setPhase('ended');
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Disconnect failed');
      } finally {
        setLoading(false);
      }
    },
    [cleanupWebRtc, session]
  );

  const sendControlEvent = useCallback(
    (event: Record<string, unknown>) => {
      if (!session) return;
      sendWs('control-event', {
        sessionId: session.sessionId,
        engineerId: session.engineerId,
        customerUserId: session.customerUserId,
        event,
      });
    },
    [sendWs, session]
  );

  return (
    <EngineerRemoteSupportContext.Provider
      value={{
        session,
        phase,
        loading,
        error,
        remoteVideoRef,
        loadSession,
        attachSession,
        disconnect,
        sendControlEvent,
        clearError: () => setError(null),
        remoteStream,
      }}
    >
      {children}
    </EngineerRemoteSupportContext.Provider>
  );
}

export function useEngineerRemoteSupport() {
  const ctx = useContext(EngineerRemoteSupportContext);
  if (!ctx) {
    throw new Error('useEngineerRemoteSupport must be used within EngineerRemoteSupportProvider');
  }
  return ctx;
}
